/** @format */

import {SnackbarProvider, VariantType, useSnackbar} from 'notistack';
import {useState, useEffect} from 'react';
import {Container, Box, Stack} from '@mui/material';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import {Auth} from 'aws-amplify';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {PrimaryButton} from '../../component/BasicButton';
//Style import
import '../../css/common/common.css';

import {useUserInfo} from '../my-site/MainMySite';
import {format} from '../../define';

/**
 * パスワード変更
 * @returns JSX
 */
const ChangePassword = () => {
  const trans = useTranslation().t; //翻訳関数
  const {enqueueSnackbar} = useSnackbar();
  //メールアドレスを取得
  const {email} = useUserInfo();

  const [password, setPassword] = useState(''); //現在のパスワード
  const [newPassword, setNewPassword] = useState(''); //新しいパスワード
  const [reEnterNewPassword, setReEnterNewPassword] = useState(''); //新しいパスワード（2回目の入力）
  const [chgPwdState, setChgPwdState] = useState(''); //パスワード未一致、現在のパスワード入力ミス
  const [chgNewPwdState, setChgNewPwdState] = useState(''); //パスワードのフォーマットの合否
  const [chgPwdBtnDisabled, setChgPwdBtnDisabled] = useState(true); //CHANGE PASSWORDボタン非表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [passwordError, setPasswordError] = useState(false); //現在のパスワード
  const [newPasswordError, setNewPasswordError] = useState(false); //新しいパスワード
  const [reEnterNewPasswordError, setReEnterNewPasswordError] = useState(false); //新しいパスワード（2回目の入力）

  //入力データのエラーチェック
  useEffect(() => {
    if (
      password === '' ||
      newPassword === '' ||
      reEnterNewPassword === '' ||
      passwordError ||
      newPasswordError ||
      reEnterNewPasswordError
    ) {
      setChgPwdBtnDisabled(true);
    } else {
      setChgPwdBtnDisabled(false);
    }
  }, [
    password,
    newPassword,
    reEnterNewPassword,
    passwordError,
    newPasswordError,
    reEnterNewPasswordError,
  ]);

  /**
   * CHANGE PASSWORDボタン押下時の処理
   * @returns
   */
  const handleSubmit = () => {
    setChgPwdState('');
    setChgNewPwdState('');
    setChgPwdBtnDisabled(true);
    setAPIWait(true);

    //新しいパスワードが確認パスワードと一致しない場合はエラーメッセージを表示
    if (newPassword !== reEnterNewPassword) {
      setChgPwdState('ChangePasswordFailure');
      setChgPwdBtnDisabled(false);
      setAPIWait(false);
      return;
    }
    //新しいパスワードが条件を満たしていない場合はエラーメッセージを表示
    if (
      newPassword.search(/[A-Z]/) === -1 ||
      newPassword.search(/[a-z]/) === -1 ||
      newPassword.search(/[0-9]/) === -1 ||
      newPassword.search(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/) === -1 ||
      newPassword.search(email.slice(0, email.indexOf('@'))) !== -1 ||
      newPassword.length < format.PASSWORD_LENGTH_MIN
    ) {
      setChgNewPwdState('ChangeNewPasswordFailure');
      setChgPwdBtnDisabled(false);
      setAPIWait(false);
      return;
    }

    //ユーザ情報の生成
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    const userPool = new CognitoUserPool({
      UserPoolId: process.env.REACT_APP_userPoolId
        ? process.env.REACT_APP_userPoolId
        : '',
      ClientId: process.env.REACT_APP_userPoolWebClientId
        ? process.env.REACT_APP_userPoolWebClientId
        : '',
    });
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    //パスワードの変更処理
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: () => {
        Auth.currentAuthenticatedUser()
          .then(user => {
            return Auth.changePassword(user, password, newPassword);
          })
          .then(response => {
            const variant: VariantType = 'success';
            enqueueSnackbar(
              trans('ChangePassword.executionResult.successMsg'),
              {
                variant,
              }
            );
          })
          .catch(err => {
            apiErrorAlert(trans);
            const variant: VariantType = 'error';
            enqueueSnackbar(
              trans('ChangePassword.executionResult.failureMsg'),
              {
                variant,
              }
            );
          })
          .finally(() => {
            setChgPwdBtnDisabled(false);
            setAPIWait(false);
          });
      },
      //現在のパスワードが間違っている場合エラーメッセージを表示
      onFailure() {
        setChgPwdState('ChangePasswordFailure');
        setChgPwdBtnDisabled(false);
        setAPIWait(false);
      },
    });
  };

  return (
    <Container fixed className="accountSetting">
      <Stack spacing={3}>
        {/** タイトル */}
        <Box>
          <h1>{trans('ChangePassword.title')}</h1>
        </Box>
        <Box>
          <Stack spacing={1}>
            <Box className="accountSetting__text__instruction">
              <h3>{trans('ChangePassword.instruction')}</h3>
            </Box>
            {/* 現在のパスワードが認証されないor新しいパスワードと確認用パスワードが不一致の場合はメッセージを表示 */}
            {chgPwdState === 'ChangePasswordFailure' ? (
              <Box className="accountSetting__text--error">
                {trans('ChangePassword.errMsg.inputErr')}
              </Box>
            ) : (
              <></>
            )}
            {/* 新しいパスワードが許容条件を満たしていない場合はメッセージを表示 */}
            {chgNewPwdState === 'ChangeNewPasswordFailure' ? (
              <Box className="accountSetting__text--error">
                {trans('ChangePassword.errMsg.invalidPassword')}
              </Box>
            ) : (
              <></>
            )}
            {/* 現在のパスワード */}
            <ChkInputTextbox
              setValue={setPassword}
              setError={setPasswordError}
              chkInputTxtOK={format.PASSWORD_PATTERN_OK}
              chkLength={format.PASSWORD_LENGTH_MAX}
              type="password"
              label={trans('ChangePassword.txtField.label.password')}
              value={password}
              id="mv-loginPasswordInputBox"
              class="accountSetting__textBox--password"
              helperText=" "
              errHelperText={trans('ChangePassword.txtField.errHelperTxt')}
              variant="standard"
              autoFocus={true}
            />
            {/* 新しいパスワード */}
            <ChkInputTextbox
              setValue={setNewPassword}
              setError={setNewPasswordError}
              chkInputTxtOK={format.PASSWORD_PATTERN_OK}
              chkLength={format.PASSWORD_LENGTH_MAX}
              type="password"
              label={trans('ChangePassword.txtField.label.newPassword')}
              value={newPassword}
              id="mv-loginNewPasswordInputBox"
              class="accountSetting__textBox--password"
              helperText={trans(
                'ChangePassword.txtField.helperTxt.newPassword',
                {
                  length: format.PASSWORD_LENGTH_MIN,
                }
              )}
              errHelperText={trans('ChangePassword.txtField.errHelperTxt')}
              variant="standard"
            />
            {/* 確認用新しいパスワード */}
            <ChkInputTextbox
              setValue={setReEnterNewPassword}
              setError={setReEnterNewPasswordError}
              chkInputTxtOK={format.PASSWORD_PATTERN_OK}
              chkLength={format.PASSWORD_LENGTH_MAX}
              type="password"
              label={trans('ChangePassword.txtField.label.confirmNewPassword')}
              value={reEnterNewPassword}
              id="mv-loginReNewPasswordInputBox"
              class="accountSetting__textBox--password"
              helperText=" "
              errHelperText={trans('ChangePassword.txtField.errHelperTxt')}
              variant="standard"
            />
            {/* パスワード変更ボタン */}
            <Box className="accountSetting__button">
              <PrimaryButton
                onClick={handleSubmit}
                buttonText={trans('ChangePassword.buttonTxt.changePassword')}
                id="mv-changePasswordBtn"
                disabled={chgPwdBtnDisabled}
                apiWait={apiWait}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};
const Main = () => {
  //SnackbarProviderはuseSnackbar()と同じコンポーネントにできないため分割した
  return (
    <SnackbarProvider maxSnack={3}>
      <ChangePassword />
    </SnackbarProvider>
  );
};
export default Main;
